import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../components/auth/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useI18n } from '@/i18n';
import './login.css';

const API_BASE = (((import.meta as any).env?.VITE_API_URL) || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/,'');

type Step = 'identifier' | 'password';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { loginSuccess } = useAuth();
  const { t } = useI18n();
  useDocumentTitle(t('app.title'));
  const [step, setStep] = useState<Step>('identifier');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const prevHeightRef = useRef<number | null>(null);

  useEffect(() => { setMounted(true); }, []);

  const translateLoginError = (detail?: string) => {
    if (!detail) return null;
    const message = detail.trim();
    const map: Record<string, string> = {
      'Please enter the correct password': 'login.passwordError',
      'Please input a correct email': 'login.identifierError',
      'Identifier is required': 'login.errors.identifierRequired',
      'Identifier and password are required': 'login.errors.credentialsRequired',
      'Please wait until the moderator accepts your account or contact the supply team.': 'login.errors.awaitingApproval',
      'Please wait untill moderator accepts your account': 'login.errors.awaitingApproval',
      'Insuficient information, please contact Fatih +998334307565 for further assistance.': 'login.errors.holdContact'
    };
    const key = map[message];
    return key ? t(key) : null;
  };

  async function checkIdentifier(e?: React.FormEvent) {
    if (e) e.preventDefault();
    setError('');
    const trimmed = identifier.trim();
    if (!trimmed) {
      setError(t('login.identifierError'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login/check-identifier`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: trimmed })
      });
      const data = await res.json();
      if (!res.ok || !data.exists) {
        setError(t('login.identifierError'));
        return;
      }
      // Capture current height before changing content
      if (cardRef.current) {
        prevHeightRef.current = cardRef.current.getBoundingClientRect().height;
      }
      setStep('password');
      setShowPassword(false);
    } catch (err) {
      setError(t('login.networkError'));
    } finally {
      setLoading(false);
    }
  }

  async function verifyPassword(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!password) {
      setError(t('login.passwordError'));
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: identifier.trim(), password }),
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        const detail = data?.detail;
        const translated = translateLoginError(detail);
        const msg = translated || detail || t('login.passwordError');
        setError(msg);
        return;
      }
      const profileLoaded = await loginSuccess();
      if (!profileLoaded) {
        console.warn('[Login] Login succeeded but profile fetch failed, showing error');
        setError('Login succeeded but could not load your profile. The /api/profiles/me endpoint may be unreachable. Please contact support or try again.');
        return;
      }
      setSuccessMsg(t('login.loginSuccess'));
      navigate('/profiles/my-requests');
    } catch (err) {
      console.error('[Login] verifyPassword error:', err);
      setError(t('login.networkError'));
    } finally {
      setLoading(false);
    }
  }

  // Smoothly animate card height when step changes (identifier <-> password)
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;
    const startH = prevHeightRef.current;
    if (startH == null) return;
    // Prepare for height transition
    card.style.height = `${startH}px`;
    card.classList.add('resizing');
    // Next frame, set to new content height
    requestAnimationFrame(() => {
      const endH = card.scrollHeight;
      card.style.height = `${endH}px`;
    });
    const onEnd = () => {
      card.classList.remove('resizing');
      card.style.height = '';
      prevHeightRef.current = null;
    };
    card.addEventListener('transitionend', onEnd, { once: true });
    return () => card.removeEventListener('transitionend', onEnd);
  }, [step]);

  return (
    <div className="login-wrapper">
      {successMsg && (
        <div className="toast toast-success" role="status" aria-live="polite">{successMsg}</div>
      )}
      <div ref={cardRef} className={`login-card ${mounted ? 'show' : ''}`}>
        <h1 className="login-title">{t('login.title')}</h1>

        {step === 'identifier' && (
          <form onSubmit={checkIdentifier} className="login-form">
            <label htmlFor="identifier" className="login-label">{t('login.identifierLabel')}</label>
            <input
              id="identifier"
              type="text"
              className="login-input"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t('login.identifierPlaceholder')}
              autoFocus
            />
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? t('login.checking') : t('login.continue')}
            </button>
          </form>
        )}

        {step === 'password' && (
          <form onSubmit={verifyPassword} className="login-form">
            <div className="login-confirm">
              <div className="login-confirm-label">{t('login.emailPhone')}</div>
              <div className="login-confirm-value">{identifier}</div>
              <button
                type="button"
                className="login-edit"
                onClick={() => {
                  if (cardRef.current) {
                    prevHeightRef.current = cardRef.current.getBoundingClientRect().height;
                  }
                  setStep('identifier');
                }}
              >
                {t('login.change')}
              </button>
            </div>

            <label htmlFor="password" className="login-label">{t('login.passwordLabel')}</label>
            <div className="password-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="login-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
              />
              <button
                type="button"
                className={`visibility-toggle ${showPassword ? 'visible' : ''}`}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                onClick={() => setShowPassword(v => !v)}
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" className="login-button" disabled={loading}>
              {loading ? t('login.signingIn') : t('login.loginButton')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;
